import { Box, getColorCSSVar } from '@iheartjane/reefer';
import type { ColorKey, PaddingProperties, Spacing } from '@iheartjane/reefer';
import { clsx } from 'clsx';
import type { CSSProperties, PropsWithChildren } from 'react';

import styles from './cardGrid.module.css';

export interface CardGridProps extends PropsWithChildren, PaddingProperties {
  /** Additional CSS class */
  className?: string;
  /** Column gap (desktop, px). Default: 24 */
  gap?: Spacing;
  /** HTML id attribute */
  id?: string;
  /** Single-column list layout. Default: false */
  listView?: boolean;
  /** Minimum card width for auto-fill columns. Default: 200 */
  minCardWidth?: Spacing | number;
  /** Mobile column behavior. number = fixed columns, 'auto' = auto-fill. Default: 2 */
  mobileColumns?: Spacing | number;
  /** Column gap (mobile, px). Default: 16 */
  mobileGap?: Spacing;
  /** Row gap override, falls back to gap/mobileGap */
  rowGap?: Spacing;
}

interface PlacementProps extends PropsWithChildren, PaddingProperties {
  /** Dynamic gradient end color. When provided, renders a gradient background. */
  background?: ColorKey;
}

function Placement({ children, background, ...paddingProps }: PlacementProps) {
  const placementStyle: CSSProperties | undefined = background
    ? {
        '--placement-gradient-color': getColorCSSVar(background),
      }
    : undefined;

  return (
    <Box
      className={styles['placement']}
      style={placementStyle}
      {...paddingProps}
    >
      {background && <div className={styles['placement_background']} />}
      <div className={styles['placement_content']}>{children}</div>
    </Box>
  );
}

function CardGridComponent({
  children,
  className,
  id,
  minCardWidth = 200,
  mobileColumns = 2,
  gap = 24,
  mobileGap = 16,
  rowGap,
  listView = false,
  ...paddingProps
}: CardGridProps) {
  const cssVars: CSSProperties = {
    '--grid-min-card-width': `${minCardWidth}px`,
    '--grid-gap': `${gap}px`,
    '--grid-mobile-gap': `${mobileGap}px`,
    ...(typeof mobileColumns === 'number' && {
      '--grid-mobile-cols': mobileColumns,
    }),
    ...(rowGap !== undefined && { '--grid-row-gap': `${rowGap}px` }),
  };

  return (
    <Box
      className={clsx(
        styles['cardGrid'],
        listView && styles['cardGrid_listView'],
        mobileColumns === 'auto' && styles['cardGrid_autoFillMobile'],
        className
      )}
      id={id}
      style={cssVars}
      p={1 as any}
      {...paddingProps}
    >
      {children}
    </Box>
  );
}

export const CardGrid = Object.assign(CardGridComponent, {
  Placement,
});
